import { Environment } from '../../../types/config';
import { getSettingsService, SendingPoolSettingsService } from '../SettingsService';
import { isValidPool, normalizePool } from './poolUtils';

export type SendingPoolOptions = {
  env: Environment;
  settings?: SendingPoolSettingsService;
};

/**
 * Manages the Bixel sending pool host information and logic.
 */
export class SendingPool {
  defaultPool = 'bixel.io';

  protected env: Environment;
  protected settings: SendingPoolSettingsService;

  pool: string | null = null;

  constructor(options: SendingPoolOptions) {
    this.env = options.env;
    this.settings = options.settings ?? getSettingsService('sendingPool');

    this.init();
  }

  /** Returns true if the current pool is set. */
  get hasPool() {
    return typeof this.pool === 'string';
  }

  /** Returns true if the data is persisted to storage. */
  get persisted() {
    const { environment, host, hostUrl, timestamp } = this.settings.pick(
      'environment',
      'host',
      'hostUrl',
      'timestamp',
    );

    // only support bixel data from the last hour.
    if ((timestamp ?? 0) < Date.now() - 3600000) {
      this.clear();
      return false;
    }

    // double check that we have a host or hostUrl from the settings since
    // brand new settings will have a valid timestamp.
    if (!host && !hostUrl) {
      return false;
    }

    // make sure environments match.
    if (!environment || this.env !== environment) {
      return false;
    }

    if (this.hasPool && host !== this.pool) {
      return false;
    }

    return true;
  }

  /** Removes any settings from storage. */
  clear() {
    this.settings.clear();
  }

  /**
   * Gets the currently set pool for the environment/port combination or the default
   * if not set.
   * @arg env: The environment to get the pool for (can add dev. or bb. depending on state)
   * @arg port: The port to add to the pool
   * @returns a pool host used to communciate with Bixel.
   */
  getPool(env: Environment | null = null, port: number | null = null) {
    const pool = this.hasPool ? this.pool : this.defaultPool;
    const thisEnv = env ?? this.env;

    let environment = '';
    switch (thisEnv) {
      case 'prod':
        if (this.pool === 'bixel.io') {
          environment = 'bb.';
        }
        break;
      default:
        environment = 'dev.';
        break;
    }

    const thisPort = port ? `:${port}` : '';
    return `${environment}${pool}${thisPort}`;
  }

  /**
   * Gets the Bixel pool host as an https:// URL.
   * @arg env: The environment to get the pool for (can add dev. or bb. depending on state)
   * @arg port: The port to add to the pool
   * @returns a string containing a URL to the Bixel host.
   */
  getPoolUrl(env: Environment | null = null, port: number | null = null) {
    return `https://${this.getPool(env, port)}`;
  }

  /**
   * Initializes this instance.
   */
  init() {
    if (!this.persisted) {
      return;
    }

    const { host, hostUrl } = this.settings.pick('host', 'hostUrl');
    if (typeof host === 'string') {
      this.setPool(host);
    } else if (typeof hostUrl === 'string') {
      this.setPool(hostUrl);
    }
  }

  /**
   * Saves the current pool information to the setting storage.
   */
  save() {
    if (!this.hasPool) {
      this.setPool(this.getPool());
    }

    this.settings.setBatch(
      {
        hostUrl: this.getPoolUrl(),
        host: this.pool,
      },
      true,
    );
  }

  /**
   * Updates the current pool settings based off the pool provided.
   * @arg pool: The pool host to use for future calls.
   */
  setPool(pool: string) {
    const thisPool = normalizePool(pool);
    if (isValidPool(thisPool) && this.pool !== thisPool) {
      this.pool = thisPool;
    }
  }
}
